import { useState } from 'react'

import { Button, Chip, Collapse, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  MedicalServices as MedicalServicesIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material'

import colors from '@/assets/scss/_themes-vars.module.scss'

import { getConflictTypeColor } from '../../lib/conflictResolverHelpers'
import { ConflictDetails, ConflictDetailsProps } from './ConflictDetails'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/macro'

type ConflictRowProps = ConflictDetailsProps

export function ConflictRow({ conflict, loading, onResolveConflict, onResolvePatientDuplicate }: ConflictRowProps) {
  useLingui()
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpanded = () => setIsExpanded((prevIsExpaned) => !prevIsExpaned)

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => toggleExpanded()}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ConflictIcon entityType={conflict.entityType} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {conflict.entityType}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: colors.gray600 }}>
            {conflict.entityId}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip label={conflict.conflictType} size="small" color={getConflictTypeColor(conflict.conflictType)} variant="filled" />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontSize: '13px' }}>
            {new Date(conflict.detectedAt).toLocaleString()}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Button size="small" variant="outlined" onClick={() => toggleExpanded()}>
            {isExpanded ? <Trans>Hide</Trans> : <Trans>Resolve</Trans>}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
          <Collapse in={isExpanded}>
            <ConflictDetails
              conflict={conflict}
              loading={loading}
              onResolveConflict={onResolveConflict}
              onResolvePatientDuplicate={onResolvePatientDuplicate}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

function ConflictIcon({ entityType }: { entityType: string }) {
  switch (entityType) {
    case 'patient':
      return <PersonIcon />
    case 'consultation':
      return <MedicalServicesIcon />
    case 'document':
      return <DescriptionIcon />
    default:
      return <WarningIcon />
  }
}
